/**
 * @fileoverview Spanish string bundle for `@puffin-core/login-shell`.
 *
 * AAS-I18N-002 W2 / ruling 2 (Q-QF4-5, 2026-08-09). Before this file existed the
 * package shipped `en.js` ONLY, and `resolveString(t, key)` fell back to those
 * English defaults whenever the consumer passed no `t` — which was **every** render
 * site in the ecosystem (measured: 12 `<LoginShell>` renders across 11 files, `t=`
 * passed at zero of them). The login screen therefore rendered English under
 * `<html lang="es">` in all ten apps: RECIPE miss-class 9 (shared-package surface),
 * plus a WCAG SC 3.1.1 failure against the `lang` attribute Q-QF4-3 had just fixed.
 *
 * ⚠️ **Keys are FLAT and interpolation is SINGLE-CURLY (`{email}`), deliberately.**
 * The flat shape mirrors `DEFAULT_LOGIN_STRINGS` exactly — the same key set resolves
 * whether it comes from i18next or from the English fallback, so the two paths can
 * never diverge. Single-curly placeholders pass through i18next's `{{…}}`
 * interpolator untouched and are expanded by this package's own `interpolate()`,
 * which makes placeholder expansion identical on the wired and unwired paths.
 * (RECIPE §6: react-i18next's no-instance `t` does NOT interpolate — page-shell
 * shipped a literal `{{label}}` into an `aria-label` for exactly that reason.)
 *
 * Register: **usted** (ecosystem rule, ruled 2026-08-08).
 *
 * Every key here MUST exist in `en.js` and vice versa —
 * `tests/unit/loadLoginStrings.test.js` fails the build otherwise (a key present in
 * one locale only is RECIPE miss-class 5: it renders English forever at a census
 * of zero).
 */

export const LOGIN_STRINGS_ES = Object.freeze({
  /* Page chrome */
  'login.title': 'Le damos la bienvenida',
  'login.subtitle': 'Inicie sesión para continuar',
  'login.pageTitle': 'Iniciar sesión',
  'login.backToHome': 'Volver al inicio',

  /* Accessible names (assistive tech only) */
  'login.form.ariaLabel': 'Formulario de inicio de sesión',

  /* Field labels */
  'login.email.label': 'Correo electrónico',
  'login.password.label': 'Contraseña',
  'login.remember.plain': 'Recordarme',

  /* Buttons */
  'login.submit': 'Iniciar sesión',
  'login.submit.loading': 'Iniciando sesión...',
  'login.unlock': 'Desbloquear',
  'login.unlock.loading': 'Desbloqueando...',
  'login.passkey.submit': 'Iniciar sesión con clave de acceso',
  'login.passkey.submitSecurityKey': 'Iniciar sesión con llave de seguridad',
  'login.passkey.tryAgain': 'Vuelva a intentarlo',
  'login.passkey.unlock': 'Desbloquear con clave de acceso',
  'login.passkey.unlockSecurityKey': 'Desbloquear con llave de seguridad',

  /* Links */
  'login.forgot.prompt': '¿Olvidó su contraseña?',
  'login.forgot.action': 'Enviar correo de restablecimiento',
  'login.contact.prompt': '¿Necesita acceso?',
  'login.contact.action': 'Contacte con el gerente de su tienda',
  'login.notYourCompany.prompt': '¿No es su empresa?',
  'login.notYourCompany.action': 'Inicie sesión en otra',

  /* N152 — el inicio de sesión retenido en el sitio público: la persona se inscribe en la página de su empresa. */
  'login.mfaEnrollmentHandoff.title': 'Configure la verificación en dos pasos',
  'login.mfaEnrollmentHandoff.body':
    'Su cuenta necesita volver a configurar la verificación en dos pasos antes de iniciar sesión. Continúe en la página de inicio de sesión de su empresa para configurarla.',
  'login.mfaEnrollmentHandoff.continue': 'Continuar a su página de inicio de sesión',
  'login.mfaEnrollmentHandoff.back': 'Volver',
  // SECREQ S6d (owner ruling Q5).
  'login.passkeySetupHandoff.title': 'Configure una clave de acceso',
  'login.passkeySetupHandoff.body':
    'Su empresa exige una clave de acceso para su función, y {appName} no puede configurarla. Configúrela en una de estas aplicaciones y luego vuelva a iniciar sesión aquí.',
  'login.passkeySetupHandoff.openApp': 'Configurar en {appName}',
  'login.passkeySetupHandoff.back': 'Volver',

  /* Auth method selector */
  'method.emailPassword': 'Correo y contraseña',
  'method.passkey': 'Clave de acceso',
  'method.pin': 'PIN',
  'method.selectorLabel': 'Seleccione el método de autenticación',
  'method.checking': 'Comprobando...',
  'method.unavailableTitle': 'No disponible en este momento',
  'method.unavailableHint': 'No está disponible en este momento. El motivo se indica abajo.',
  'method.unavailableLine': '{method}: {reason}',

  'login.passkey.fallback': 'Usar correo y contraseña en su lugar',

  /* PalmPuffin Authenticator push 2FA (AUTHENTICATOR-CHALLENGE-FANOUT, 2026-08-29). */
  'authenticator.title': 'Aprobar inicio de sesión',
  'authenticator.subtitle': 'Ingrese este número en PalmPuffin para aprobar:',
  'authenticator.numberAria': 'Número de aprobación {number}',
  'authenticator.waiting': 'Esperando aprobación...',
  'authenticator.useCodeInstead': 'Usar un código en su lugar',
  'authenticator.cancel': 'Cancelar',
  'authenticator.status.approved': 'Aprobado',
  'authenticator.status.denied': 'Este inicio de sesión fue rechazado en su dispositivo.',
  'authenticator.status.expired': 'Esta solicitud expiró. Vuelva a iniciar sesión.',
  'authenticator.error.network': 'Error de red. Revise su conexión e inténtelo de nuevo.',
  'authenticator.error.incomplete':
    'No se pudo completar el inicio de sesión. Vuelva a iniciar sesión.',
  'authenticator.error.code':
    'Ese código no es correcto. Verifique su aplicación de autenticación e inténtelo de nuevo.',
  'authenticator.error.rateLimited':
    'Demasiados intentos. Espere un momento y vuelva a intentarlo.',
  'authenticator.error.codeCheckFailed': 'No se pudo verificar su código. Inténtelo de nuevo.',
  'authenticator.codePrompt': 'Ingrese el código de 6 dígitos de su aplicación de autenticación:',
  'authenticator.submitCode': 'Verificar código',
  'authenticator.backToPush': 'Volver a la solicitud de aprobación',

  /* Passkey states */
  'passkey.unsupported':
    'La autenticación con clave de acceso no es compatible con este dispositivo o navegador. Use su correo electrónico y su contraseña para iniciar sesión.',
  'passkey.noPlatform':
    'No se detectó ningún autenticador de plataforma (como Touch ID o Windows Hello). Aún puede usar una llave de seguridad.',
  'passkey.loading': 'Esperando la autenticación con clave de acceso...',
  /* Passkey sign-in failures (USER-FACING-GEMS L1). */
  'passkey.error.cancelled':
    'Se canceló el inicio de sesión con clave de acceso. Vuelva a intentarlo o use su correo electrónico y su contraseña.',
  'passkey.error.notRecognized':
    'Esa clave de acceso no está registrada para esta cuenta. Pruebe con otra clave de acceso o use su correo electrónico y su contraseña.',
  'passkey.error.rateLimited': 'Demasiados intentos. Espere un momento y vuelva a intentarlo.',
  'passkey.error.unavailable':
    'El inicio de sesión con clave de acceso no está disponible por un momento. Vuelva a intentarlo, o use su correo electrónico y su contraseña.',
  'passkey.error.failed':
    'No se completó el inicio de sesión con clave de acceso. Compruebe su conexión y vuelva a intentarlo, o use su correo electrónico y su contraseña.',

  /* Password reset modal */
  'modal.passwordReset.title': 'Restablecer contraseña',
  'modal.passwordReset.submit.loading': 'Enviando...',
  'modal.passwordReset.passwordChangedBody':
    'Su contraseña se ha actualizado. Ya puede iniciar sesión con su nueva contraseña.',
  'modal.passwordReset.close': 'Cerrar',

  /* Password reset multi-step (OTP) flow */
  'modal.passwordReset.multiStep.emailBody':
    'Introduzca su dirección de correo electrónico y le enviaremos un código de verificación de 6 dígitos.',
  'modal.passwordReset.multiStep.emailSubmit': 'Enviar código de verificación',
  'modal.passwordReset.multiStep.codeBody':
    'Enviamos un código de 6 dígitos a {email}. Introdúzcalo a continuación para continuar.',
  'modal.passwordReset.multiStep.codeLabel': 'Código de verificación',
  'modal.passwordReset.multiStep.codeSubmit': 'Verificar código',
  'modal.passwordReset.multiStep.resendCode': 'Reenviar código',
  'modal.passwordReset.multiStep.newPasswordBody':
    'Elija una nueva contraseña. Debe cumplir todos los requisitos que se indican a continuación.',
  'modal.passwordReset.multiStep.newPasswordLabel': 'Nueva contraseña',
  'modal.passwordReset.multiStep.confirmPasswordLabel': 'Confirme la nueva contraseña',
  'modal.passwordReset.multiStep.newPasswordSubmit': 'Cambiar contraseña',
  'modal.passwordReset.stepOf': 'Paso {current} de {total}',
  'modal.passwordReset.form.email': 'Formulario para restablecer la contraseña',
  'modal.passwordReset.form.code': 'Formulario del código de verificación',
  'modal.passwordReset.form.newPassword': 'Formulario de la nueva contraseña',
  'modal.passwordReset.multiStep.differentEmail': 'Usar otro correo electrónico',
  'modal.passwordReset.multiStep.resendIn': 'Reenviar el código en {seconds} s',
  'modal.passwordReset.error.network':
    'No pudimos conectar con el servidor. Revise su conexión e inténtelo de nuevo.',
  'modal.passwordReset.error.captcha': 'Complete la verificación e inténtelo de nuevo.',
  'modal.passwordReset.error.rateLimited':
    'Demasiadas solicitudes. Espere unos minutos e inténtelo de nuevo.',
  'modal.passwordReset.error.tooSoon': 'Espere {seconds} segundos antes de solicitar otro código.',
  'modal.passwordReset.error.codeFormat':
    'Introduzca el código de 6 dígitos del correo electrónico.',
  'modal.passwordReset.error.codeInvalid': 'Ese código es incorrecto o ha caducado.',
  'modal.passwordReset.error.codeLocked':
    'Demasiados códigos incorrectos. Solicite un código nuevo para continuar.',
  'modal.passwordReset.error.requirementsUnmet':
    'Su contraseña no cumple todos los requisitos que se indican a continuación.',
  'modal.passwordReset.error.mismatch': 'Las contraseñas no coinciden.',
  'modal.passwordReset.error.sessionExpired':
    'Su sesión de restablecimiento caducó. Solicite un código nuevo para continuar.',
  'modal.passwordReset.error.passwordRejected':
    'No se puede usar esa contraseña. Elija una que cumpla todos los requisitos y que no haya usado recientemente.',
  'modal.passwordReset.error.invalidField.email':
    'Introduzca la dirección de correo electrónico de su cuenta.',
  'modal.passwordReset.error.invalidField.subdomain':
    'No pudimos saber para qué empresa es este restablecimiento. Abra la página de inicio de sesión desde la dirección de su empresa e inténtelo de nuevo.',
  'modal.passwordReset.error.invalidField.other':
    'Faltaba parte de la solicitud. Cierre esta ventana y vuelva a empezar.',
  'modal.passwordReset.error.notRecorded':
    'Su cambio no se realizó porque no se pudo registrar. Inténtelo de nuevo en un momento.',
  'modal.passwordReset.error.failed': 'Algo salió mal. Inténtelo de nuevo.',

  /* Rate limit. The client throttle's notice lives in `@puffin-core/form-security` (Q-UMB-21). */
  'login.error.rateLimited':
    'Demasiados intentos. Espere {seconds} segundos antes de volver a intentarlo.',

  /* LOGIN-CONVERGENCE W7 (2026-10-11): un inicio de sesión rechazado. */
  'login.error.credentials': 'Correo electrónico o contraseña no válidos',
  'login.error.mfa': 'Código de verificación no válido',
  'login.error.server': 'Error del servidor. Vuelva a intentarlo.',
  'login.error.locationsUnavailable':
    'No pudimos cargar sus tiendas. Vuelva a intentarlo en un momento.',
  'login.error.network': 'Error de red. Compruebe su conexión.',
  'login.error.rateLimit': 'Demasiados intentos. Espere y vuelva a intentarlo.',
  'login.error.badRequest': 'Hubo un problema con su solicitud. Vuelva a intentarlo.',
  'login.error.mfaEnrollmentRequired':
    'Debe volver a configurar la verificación en dos pasos antes de iniciar sesión.',
  'login.error.passkeySetupRequired':
    'Su empresa exige una clave de acceso para su función. Configure una para terminar de iniciar sesión.',
  'login.error.pinSetupRequired':
    'Su empresa exige un PIN para su función. Configure un PIN en esta caja para terminar de iniciar sesión.',
  'login.error.deviceLoginNotPermitted':
    'Su cuenta no tiene permiso para iniciar sesión en este dispositivo. Comuníquese con el gerente de su tienda.',
  'login.error.mfaCodeInvalid':
    'Ese código no coincide. Revise su autenticador y vuelva a intentarlo.',
  'login.error.mfaChallengeInvalid':
    'Su sesión de verificación ya no es válida. Vuelva a iniciar sesión.',
  'login.error.mfaLocked': 'Demasiados intentos. Vuelva a iniciar sesión.',
  'login.error.mfaSessionExpired': 'Su sesión de verificación caducó. Vuelva a iniciar sesión.',
  'login.error.mfaMethodNotEnrolled':
    'Ese método de verificación no está configurado para su cuenta. Vuelva a iniciar sesión.',
  'login.error.mfaMethodUnsupported':
    'Ese método de verificación no está disponible aquí. Vuelva a iniciar sesión.',
  'login.error.mfaUnavailable':
    'La verificación no está disponible en este momento. Vuelva a intentarlo en un momento.',
  'login.error.credentialsRequired':
    'Escriba su correo electrónico y su contraseña para iniciar sesión.',
  'login.error.captchaRequired': 'Complete la verificación de seguridad para iniciar sesión.',
  'login.error.credentialsInvalid': 'Revise lo que escribió y vuelva a intentarlo.',
  'login.refusal.captchaFailed':
    'La verificación de seguridad no se superó. Complétela de nuevo para iniciar sesión.',
  'login.refusal.accountNotActive':
    'Esta cuenta no está activa. Pida a quien gestiona su acceso que lo restablezca.',
  'login.refusal.companyInactive':
    'La cuenta de esta empresa no está activa. Pida a quien la gestiona que la restablezca.',
  'login.refusal.accountUnavailable':
    'Esta cuenta no puede iniciar sesión aquí. Consulte a quien gestiona su acceso.',
  'login.refusal.signInUnavailable':
    'El inicio de sesión no está disponible en este momento. Vuelva a intentarlo en unos minutos.',
  'login.refusal.mfaNoUsableMethod':
    'Ninguno de los segundos factores que configuró se puede usar para iniciar sesión aquí. Inicie sesión donde lo configuró o pida a su administrador que restablezca su segundo factor.',
  'login.passwordReset.done': 'Su contraseña está lista. Inicie sesión con ella ahora.',
  'login.cancel': 'Cancelar',

  /* Contact manager modal */
  'modal.contact.title': 'Solicitar acceso',
  'modal.contact.formLabel': 'Formulario de solicitud de acceso',
  'modal.contact.body': 'Envíe una solicitud de acceso a {companyName}.',
  'modal.contact.fallbackCompany': 'su empresa',
  'modal.contact.name': 'Nombre completo',
  'modal.contact.email': 'Correo electrónico',
  'modal.contact.message': 'Mensaje (opcional)',
  'modal.contact.submit': 'Enviar solicitud',
  'modal.contact.submit.loading': 'Enviando...',
  'modal.contact.success':
    'Su solicitud se ha enviado al gerente de su tienda. Recibirá una confirmación por correo electrónico en breve.',
  'modal.contact.close': 'Cerrar',
  'modal.contact.error.invalid':
    'Revise su nombre y su dirección de correo electrónico e inténtelo de nuevo.',
  'modal.contact.error.rateLimited':
    'Demasiadas solicitudes. Espere unos minutos e inténtelo de nuevo.',
  'modal.contact.error.failed': 'No pudimos enviar su solicitud. Inténtelo de nuevo.',

  /* Modal generic */
  'modal.closeLabel': 'Cerrar',

  /* Subdomain prompt (cross-domain mode) */
  'subdomain.label': 'El subdominio de su empresa',
  'subdomain.validating': 'Comprobando el subdominio...',
  'subdomain.foundCompany': 'Iniciando sesión en {companyName}',
  'subdomain.welcomeBack': 'Le damos la bienvenida de nuevo a {subdomain}',
  'subdomain.useDifferentCompany': 'Usar otra empresa',
  'subdomain.cantFind': '¿No conoce su subdominio?',
  'subdomain.lookupAction': 'Buscar mi empresa',

  /* Subdomain validation failures — see the note on this group in `en.js`. */
  'subdomain.error.required': 'El subdominio es obligatorio',
  'subdomain.error.invalidFormat': 'El formato del subdominio no es válido',
  'subdomain.error.validationFailed':
    'Se produjo un error al validar el subdominio. Vuelva a intentarlo.',

  /* Subdomain lookup modal */
  'modal.subdomainLookup.title': 'Encuentre su empresa',
  'modal.subdomainLookup.description':
    'Introduzca su dirección de correo electrónico y le enviaremos información sobre el subdominio de su empresa.',
  'modal.subdomainLookup.emailLabel': 'Dirección de correo electrónico',
  'modal.subdomainLookup.submit': 'Envíeme la información de mi empresa',
  'modal.subdomainLookup.submitting': 'Enviando...',
  'modal.subdomainLookup.retry': 'Vuelva a intentarlo',
  'modal.subdomainLookup.successTitle': 'Correo enviado',
  'modal.subdomainLookup.success':
    'Si existe una cuenta con este correo electrónico, recibirá un correo con la información del subdominio de su empresa.',
  'modal.subdomainLookup.formLabel': 'Formulario de búsqueda de empresa',
  'modal.subdomainLookup.error.emailRequired': 'El correo electrónico es obligatorio',
  'modal.subdomainLookup.error.emailInvalid': 'Ingrese un correo electrónico válido',
  'modal.subdomainLookup.error.rateLimited':
    'Demasiadas solicitudes. Espere unos minutos e inténtelo de nuevo.',
  'modal.subdomainLookup.error.failed':
    'No pudimos enviar el correo electrónico. Inténtelo de nuevo.',
});

export default LOGIN_STRINGS_ES;
